import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { DividerModule } from 'primeng/divider';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { MessageService } from 'primeng/api';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';
import { BookingConfig, BlockedDate, WorkingHourDay, BookingConfigService } from '../../service/core/booking-config.service';

type BookingTab = 'general' | 'hours' | 'blocked';

@Component({
    selector: 'app-booking-config',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ButtonModule,
        CheckboxModule,
        DatePickerModule,
        DialogModule,
        DividerModule,
        InputNumberModule,
        InputTextModule,
        ProgressSpinnerModule,
        SelectModule,
        TableModule,
        TagModule,
        TextareaModule,
        ToastModule,
        ToggleSwitchModule,
        TooltipModule
    ],
    templateUrl: './booking-config.component.html',
    styleUrl: './booking-config.component.scss',
    providers: [MessageService]
})
export class BookingConfigComponent implements OnInit {

    bookingTab: BookingTab = 'general';

    // ── General Config ─────────────────────────────────────────
    loadingConfig = false;
    savingConfig = false;
    configId: string | null = null;
    config: BookingConfig = {
        timezone: 'Asia/Kolkata',
        minDurationMinutes: 30,
        maxDurationMinutes: 60,
        stepMinutes: 15,
        bufferMinutes: 10,
        advanceBookingDays: 14,
        minNoticeHours: 2,
        cancellationCutoffHours: 4,
        allowReschedule: true,
        maxBookingsPerDay: null
    };

    timezones = [
        { label: 'UTC', value: 'UTC' },
        { label: 'Asia/Kolkata (IST +5:30)', value: 'Asia/Kolkata' },
        { label: 'Asia/Dubai (GST +4:00)', value: 'Asia/Dubai' },
        { label: 'Asia/Singapore (SGT +8:00)', value: 'Asia/Singapore' },
        { label: 'Asia/Tokyo (JST +9:00)', value: 'Asia/Tokyo' },
        { label: 'Europe/London (GMT +0:00)', value: 'Europe/London' },
        { label: 'Europe/Paris (CET +1:00)', value: 'Europe/Paris' },
        { label: 'Europe/Berlin (CET +1:00)', value: 'Europe/Berlin' },
        { label: 'America/New_York (EST -5:00)', value: 'America/New_York' },
        { label: 'America/Chicago (CST -6:00)', value: 'America/Chicago' },
        { label: 'America/Denver (MST -7:00)', value: 'America/Denver' },
        { label: 'America/Los_Angeles (PST -8:00)', value: 'America/Los_Angeles' },
        { label: 'Australia/Sydney (AEDT +11:00)', value: 'Australia/Sydney' },
        { label: 'Pacific/Auckland (NZST +12:00)', value: 'Pacific/Auckland' }
    ];

    // ── Working Hours ──────────────────────────────────────────
    loadingHours = false;
    savingHours = false;
    schedule: WorkingHourDay[] = [
        { dayOfWeek: 'MON', isWorkingDay: true,  startTime: '09:00', endTime: '18:00' },
        { dayOfWeek: 'TUE', isWorkingDay: true,  startTime: '09:00', endTime: '18:00' },
        { dayOfWeek: 'WED', isWorkingDay: true,  startTime: '09:00', endTime: '18:00' },
        { dayOfWeek: 'THU', isWorkingDay: true,  startTime: '09:00', endTime: '18:00' },
        { dayOfWeek: 'FRI', isWorkingDay: true,  startTime: '09:00', endTime: '18:00' },
        { dayOfWeek: 'SAT', isWorkingDay: false, startTime: null,    endTime: null    },
        { dayOfWeek: 'SUN', isWorkingDay: false, startTime: null,    endTime: null    }
    ];

    dayLabels: Record<string, string> = {
        MON: 'Monday', TUE: 'Tuesday', WED: 'Wednesday',
        THU: 'Thursday', FRI: 'Friday', SAT: 'Saturday', SUN: 'Sunday'
    };

    // ── Blocked Dates ──────────────────────────────────────────
    loadingBlocked = false;
    blockedDates: BlockedDate[] = [];
    showAddBlockedDialog = false;
    savingBlocked = false;
    deletingBlockedId: string | null = null;

    blockedForm: { date: Date | null; type: string; reason: string; isRecurring: boolean } = {
        date: null,
        type: 'HOLIDAY',
        reason: '',
        isRecurring: false
    };

    blockedDateTypes = [
        { label: 'Holiday',     value: 'HOLIDAY'     },
        { label: 'Leave',       value: 'LEAVE'       },
        { label: 'Closure',     value: 'CLOSURE'     },
        { label: 'Maintenance', value: 'MAINTENANCE' }
    ];

    typeTagSeverity: Record<string, string> = {
        HOLIDAY: 'warn',
        LEAVE: 'info',
        CLOSURE: 'danger',
        MAINTENANCE: 'secondary'
    };

    today = new Date();

    constructor(
        private bookingService: BookingConfigService,
        private messageService: MessageService
    ) {}

    ngOnInit(): void {
        this.loadConfig();
        this.loadWorkingHours();
        this.loadBlockedDates();
    }

    // ── General Config methods ─────────────────────────────────

    loadConfig(): void {
        this.loadingConfig = true;
        this.bookingService.getConfig().subscribe({
            next: (res) => {
                if (res?.data) {
                    const d = res.data;
                    this.configId = d.id ?? null;
                    this.config = {
                        timezone: d.timezone ?? 'Asia/Kolkata',
                        minDurationMinutes: d.minDurationMinutes ?? 30,
                        maxDurationMinutes: d.maxDurationMinutes ?? 60,
                        stepMinutes: d.stepMinutes ?? 15,
                        bufferMinutes: d.bufferMinutes ?? 10,
                        advanceBookingDays: d.advanceBookingDays ?? 14,
                        minNoticeHours: d.minNoticeHours ?? 2,
                        cancellationCutoffHours: d.cancellationCutoffHours ?? 4,
                        allowReschedule: d.allowReschedule ?? true,
                        maxBookingsPerDay: d.maxBookingsPerDay ?? null
                    };
                }
                this.loadingConfig = false;
            },
            error: () => {
                this.loadingConfig = false;
            }
        });
    }

    saveConfig(): void {
        if (this.config.minDurationMinutes > this.config.maxDurationMinutes) {
            this.toast('error', 'Validation Error', 'Min duration cannot exceed max duration.');
            return;
        }
        this.savingConfig = true;
        this.bookingService.saveConfig(this.config).subscribe({
            next: (res) => {
                this.configId = res?.data ?? this.configId;
                this.savingConfig = false;
                this.toast('success', 'Config Saved', 'Booking configuration saved successfully.');
            },
            error: (err) => {
                this.savingConfig = false;
                this.toast('error', 'Save Failed', err?.message ?? 'Could not save config.');
            }
        });
    }

    // ── Working Hours methods ──────────────────────────────────

    loadWorkingHours(): void {
        this.loadingHours = true;
        this.bookingService.getWorkingHours().subscribe({
            next: (res) => {
                if (res?.data?.length) {
                    this.schedule = res.data.map((d: WorkingHourDay) => ({ ...d }));
                }
                this.loadingHours = false;
            },
            error: () => {
                this.loadingHours = false;
            }
        });
    }

    onDayToggle(day: WorkingHourDay): void {
        if (!day.isWorkingDay) {
            day.startTime = null;
            day.endTime = null;
        } else {
            day.startTime = '09:00';
            day.endTime = '18:00';
        }
    }

    saveWorkingHours(): void {
        const invalid = this.schedule.find(
            d => d.isWorkingDay && (!d.startTime || !d.endTime)
        );
        if (invalid) {
            this.toast('error', 'Validation Error', `${this.dayLabels[invalid.dayOfWeek]} is marked open but has no time set.`);
            return;
        }
        this.savingHours = true;
        this.bookingService.saveWorkingHours(this.schedule).subscribe({
            next: () => {
                this.savingHours = false;
                this.toast('success', 'Schedule Saved', 'Working hours saved successfully.');
            },
            error: (err) => {
                this.savingHours = false;
                this.toast('error', 'Save Failed', err?.message ?? 'Could not save working hours.');
            }
        });
    }

    // ── Blocked Dates methods ──────────────────────────────────

    loadBlockedDates(): void {
        this.loadingBlocked = true;
        this.bookingService.getBlockedDates().subscribe({
            next: (res) => {
                this.blockedDates = res?.data ?? [];
                this.loadingBlocked = false;
            },
            error: () => {
                this.loadingBlocked = false;
            }
        });
    }

    openAddBlockedDialog(): void {
        this.blockedForm = { date: null, type: 'HOLIDAY', reason: '', isRecurring: false };
        this.showAddBlockedDialog = true;
    }

    addBlockedDate(): void {
        if (!this.blockedForm.date) {
            this.toast('error', 'Validation Error', 'Please select a date.');
            return;
        }
        if (!this.blockedForm.reason.trim()) {
            this.toast('error', 'Validation Error', 'Please enter a reason.');
            return;
        }
        this.savingBlocked = true;
        const payload = {
            date: this.formatDate(this.blockedForm.date),
            type: this.blockedForm.type,
            reason: this.blockedForm.reason.trim(),
            isRecurring: this.blockedForm.isRecurring
        };
        this.bookingService.addBlockedDate(payload).subscribe({
            next: () => {
                this.savingBlocked = false;
                this.showAddBlockedDialog = false;
                this.toast('success', 'Date Blocked', 'Blocked date added successfully.');
                this.loadBlockedDates();
            },
            error: (err) => {
                this.savingBlocked = false;
                this.toast('error', 'Failed', err?.message ?? 'Could not add blocked date.');
            }
        });
    }

    deleteBlockedDate(id: string): void {
        this.deletingBlockedId = id;
        this.bookingService.deleteBlockedDate(id).subscribe({
            next: () => {
                this.blockedDates = this.blockedDates.filter(d => d.id !== id);
                this.deletingBlockedId = null;
                this.toast('success', 'Removed', 'Blocked date removed.');
            },
            error: (err) => {
                this.deletingBlockedId = null;
                this.toast('error', 'Failed', err?.message ?? 'Could not remove blocked date.');
            }
        });
    }

    // ── Helpers ────────────────────────────────────────────────

    private formatDate(date: Date): string {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    }

    private toast(severity: string, summary: string, detail: string): void {
        this.messageService.add({ severity, summary, detail, life: 4000 });
    }

    getTypeLabel(type: string): string {
        return this.blockedDateTypes.find(t => t.value === type)?.label ?? type;
    }

    getTypeSeverity(type: string): any {
        const map: Record<string, string> = {
            HOLIDAY: 'warn', LEAVE: 'info', CLOSURE: 'danger', MAINTENANCE: 'secondary'
        };
        return map[type] ?? 'secondary';
    }

    switchTab(tab: BookingTab): void {
        this.bookingTab = tab;
    }
}
